import { useModalContext } from '@/hooks/useModalContext';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { DataSourceType, EDataSourceCategory } from '@/models/dataSource';
import { RootState, useAppDispatch } from '@/reducers';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import DataSourceTypePanelV2 from '../slideOver_v2/DataSourceTypePanel';
import { useSelector } from 'react-redux';
import { EStatus } from '@/models/fileUpload';
import { resetUploadStatus } from '@/features/fileUploading/fileUploadingSlice';
import { clearStashedDataSource } from '../../dataSourcesSlice';
import { clearStashedRules } from '@/features/rules/rulesSlice';
import { clearIntegrationData } from '@/features/cyclr/dataSourceIntegrationSlice';
import { delay } from 'lodash';
import { DataSourceEdit } from '@/features/dataSourceEdit';
import { SLIDEOVER_TRANSITION_DURATION } from '@/providers';
import { DataSourcesTypeCardContainer } from '../commons';
import { TranslationKeys } from '@/localization';

export const AddFirstDataSource: FC = () => {
  const { openSlideOver, closeSlideOver } = useSlideOverContext();
  const { openModal } = useModalContext();
  const dispatch = useAppDispatch();

  const { data } = useGetDataSourceTypesQuery();

  const availableSources = data?.filter((data) => data.category === EDataSourceCategory.CODE || data.category === EDataSourceCategory.FILE);

  const mostUsedResources = availableSources?.filter((data) => data.category === EDataSourceCategory.CODE);
  const otherResources = availableSources?.filter((data) => data.category !== EDataSourceCategory.CODE);

  const handleClick = (dataSourceType: DataSourceType) => {
    openSlideOver({
      renderContent: DataSourceTypePanelV2,
      renderContentProps: { dataSourceType, onAddDataSourceClicked },
      showButtonInsideSlideOver: true,
    });
  };

  const fileUploadStatus = useSelector((state: RootState) => state.fileUploading.status);

  const onAddDataSourceClicked = (dataSourceType: DataSourceType) => {
    if (fileUploadStatus?.uploadingStatus !== EStatus.UPLOADING) {
      dispatch(resetUploadStatus());
    }
    dispatch(clearStashedDataSource());
    dispatch(clearStashedRules());
    dispatch(clearIntegrationData());

    closeSlideOver();
    // open data source modal after animation of slideover is finished
    delay(() => {
      openModal({
        renderContent: DataSourceEdit,
        renderContentProps: {
          dataSourceType,
        },
        fullHeight: true,
        fullWidth: true,
      });
    }, SLIDEOVER_TRANSITION_DURATION);
  };

  return (
    <div className='flex max-w-7xl flex-col gap-12'>
      <UsedResources title='data_sources.add_first_data_source.most_used_sources'>
        <DataSourcesTypeCardContainer dataSources={mostUsedResources} onCardClick={handleClick}></DataSourcesTypeCardContainer>
      </UsedResources>

      <UsedResources title='data_sources.add_first_data_source.other_resources'>
        <DataSourcesTypeCardContainer dataSources={otherResources} onCardClick={handleClick}></DataSourcesTypeCardContainer>
      </UsedResources>
    </div>
  );
};

const UsedResources: FC<PropsWithChildren<{ title: TranslationKeys }>> = ({ title, children }) => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col px-8'>
      <h1 className='text-lg font-semibold leading-7 text-gray-900'>{t(title as any)}</h1>
      {children}
    </div>
  );
};
